import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Card,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  Button,
  Stack,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { hostApiClient } from '../../hostApiClient';
import { PassOfferStatus, PassOfferSummary, PassTargetDetail } from '../../models/Pass';
import { NotificationSnackbar } from '../../components/common/NotificationSnackbar';
import PassOfferStatusChip from '../../components/passes/PassOfferStatusChip';
import PassTargetTypeChip from '../../components/passes/PassTargetTypeChip';
import { nextPassCopyTitle, type PassDuplicateLocationState } from '../../utils/passDuplicate';
import { fetchPassTargetsMap, passTargetSummaryFromIds } from '../../utils/passTargets';

type ListTab = 'offers' | 'archive';

type PassesLocationState = {
  flashMessage?: string;
};

const unitTypeLabel = (t: (key: string, fallback: string) => string, unitType: PassOfferSummary['unitType']) =>
  unitType === 'MINUTE'
    ? t('hostPasses.unitTypeHour', 'Hours')
    : t('hostPasses.unitTypeLessons', 'Lessons');

const quantityDisplay = (pass: PassOfferSummary) =>
  pass.unitType === 'MINUTE' ? pass.quantity / 60 : pass.quantity;

const formatMoneyAmount = (amount: number): string =>
  Number.isInteger(amount) ? String(amount) : amount.toFixed(2);

const packUnitRate = (pass: PassOfferSummary): number | null => {
  const units = quantityDisplay(pass);
  if (!units || units <= 0) {
    return null;
  }
  return Math.round((pass.fixedPrice / units) * 100) / 100;
};

const passValidityLabel = (
  pass: PassOfferSummary,
  t: (key: string, fallback: string, options?: Record<string, unknown>) => string
) =>
  pass.validityStartPolicy === 'FROM_FIRST_USE'
    ? t('hostPasses.validityDaysFromFirstUse', '{{count}} days from first use', {
        count: pass.validityDurationDays,
      })
    : t('hostPasses.validityDaysFromPurchase', '{{count}} days from purchase', {
        count: pass.validityDurationDays,
      });

const passSecondaryLine = (
  pass: PassOfferSummary,
  t: (key: string, fallback: string, options?: Record<string, unknown>) => string,
  targetSummary?: string | null
) => {
  const parts = [
    targetSummary?.trim() || null,
    `${quantityDisplay(pass)} ${unitTypeLabel(t, pass.unitType)}`,
    passValidityLabel(pass, t),
  ].filter(Boolean);
  return parts.join(' · ');
};

const priceUnitRateLine = (
  pass: PassOfferSummary,
  t: (key: string, fallback: string, options?: Record<string, unknown>) => string
) => {
  const rate = packUnitRate(pass);
  if (rate === null) {
    return null;
  }
  return pass.unitType === 'MINUTE'
    ? t('hostPasses.listPricePerHour', '{{amount}} {{currency}}/hour', {
        amount: formatMoneyAmount(rate),
        currency: pass.currencyCode,
      })
    : t('hostPasses.listPricePerLesson', '{{amount}} {{currency}}/lesson', {
        amount: formatMoneyAmount(rate),
        currency: pass.currencyCode,
      });
};

const purchaseCountOf = (pass: PassOfferSummary) => pass.purchaseCount ?? 0;
const activeInstanceCountOf = (pass: PassOfferSummary) => pass.activeInstanceCount ?? 0;

const HostPassesPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const [listTab, setListTab] = useState<ListTab>('offers');
  const [offerPasses, setOfferPasses] = useState<PassOfferSummary[]>([]);
  const [archivedPasses, setArchivedPasses] = useState<PassOfferSummary[]>([]);
  const [targetById, setTargetById] = useState<Map<string, PassTargetDetail>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusLoadingId, setStatusLoadingId] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuPass, setMenuPass] = useState<PassOfferSummary | null>(null);
  const [removeTarget, setRemoveTarget] = useState<PassOfferSummary | null>(null);
  const [removing, setRemoving] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const passes = listTab === 'archive' ? archivedPasses : offerPasses;

  useEffect(() => {
    const state = location.state as PassesLocationState | null;
    if (state?.flashMessage) {
      setSnackbar({ open: true, message: state.flashMessage, severity: 'success' });
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, location.state, navigate]);

  const fetchPasses = useCallback(async () => {
    try {
      setLoading(true);
      const [offers, archived] = await Promise.all([
        hostApiClient.listPassOffers(),
        hostApiClient.listPassOffers(['ARCHIVED']),
      ]);
      setOfferPasses(offers);
      setArchivedPasses(archived);
      const targets = await fetchPassTargetsMap([...offers, ...archived]);
      setTargetById(targets);
    } catch (err) {
      setError(t('hostPasses.fetchError', 'Failed to fetch pass offers'));
      console.error('Error fetching host pass offers:', err);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchPasses();
  }, [fetchPasses]);

  const updateOfferStatus = (passId: string, status: PassOfferStatus) => {
    setOfferPasses((prev) => prev.map((pass) => (pass.id === passId ? { ...pass, status } : pass)));
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuPass(null);
  };

  const openMenu = (event: React.MouseEvent<HTMLElement>, pass: PassOfferSummary) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuPass(pass);
  };

  const handleActivate = async (event: React.MouseEvent | null, passId: string) => {
    event?.stopPropagation();
    closeMenu();
    try {
      setStatusLoadingId(passId);
      const updated = await hostApiClient.activatePassOffer(passId);
      updateOfferStatus(passId, updated.status);
      setSnackbar({
        open: true,
        message: t('hostPasses.activatedSuccess', 'Pass offer activated'),
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: t('hostPasses.activateError', 'Failed to activate pass offer'),
        severity: 'error',
      });
      console.error(`Error activating host pass offer ${passId}:`, err);
    } finally {
      setStatusLoadingId(null);
    }
  };

  const handleDeactivate = async (event: React.MouseEvent | null, passId: string) => {
    event?.stopPropagation();
    closeMenu();
    try {
      setStatusLoadingId(passId);
      const updated = await hostApiClient.deactivatePassOffer(passId);
      updateOfferStatus(passId, updated.status);
      setSnackbar({
        open: true,
        message: t('hostPasses.deactivatedSuccess', 'Pass offer moved to draft'),
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: t('hostPasses.deactivateError', 'Failed to deactivate pass offer'),
        severity: 'error',
      });
      console.error(`Error deactivating host pass offer ${passId}:`, err);
    } finally {
      setStatusLoadingId(null);
    }
  };

  const handleArchive = async (event: React.MouseEvent | null, passId: string) => {
    event?.stopPropagation();
    closeMenu();
    const moved = offerPasses.find((pass) => pass.id === passId);
    try {
      setStatusLoadingId(passId);
      const updated = await hostApiClient.archivePassOffer(passId);
      setOfferPasses((prev) => prev.filter((pass) => pass.id !== passId));
      if (moved) {
        setArchivedPasses((prev) => [{ ...moved, status: updated.status }, ...prev.filter((p) => p.id !== passId)]);
      }
      setSnackbar({
        open: true,
        message: t('hostPasses.archivedSuccess', 'Pass offer moved to Archive'),
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: t('hostPasses.archiveError', 'Failed to archive pass offer'),
        severity: 'error',
      });
      console.error(`Error archiving host pass offer ${passId}:`, err);
    } finally {
      setStatusLoadingId(null);
    }
  };

  const handleRestore = async (event: React.MouseEvent | null, passId: string) => {
    event?.stopPropagation();
    closeMenu();
    const moved = archivedPasses.find((pass) => pass.id === passId);
    try {
      setStatusLoadingId(passId);
      const updated = await hostApiClient.restorePassOffer(passId);
      setArchivedPasses((prev) => prev.filter((pass) => pass.id !== passId));
      if (moved) {
        setOfferPasses((prev) => [{ ...moved, status: updated.status }, ...prev.filter((p) => p.id !== passId)]);
      }
      setSnackbar({
        open: true,
        message: t('hostPasses.restoredSuccess', 'Pass offer restored as draft'),
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: t('hostPasses.restoreError', 'Failed to restore pass offer'),
        severity: 'error',
      });
      console.error(`Error restoring host pass offer ${passId}:`, err);
    } finally {
      setStatusLoadingId(null);
    }
  };

  const handleConfirmRemove = async () => {
    if (!removeTarget) return;
    try {
      setRemoving(true);
      await hostApiClient.removePassOffer(removeTarget.id);
      setOfferPasses((prev) => prev.filter((pass) => pass.id !== removeTarget.id));
      setArchivedPasses((prev) => prev.filter((pass) => pass.id !== removeTarget.id));
      setSnackbar({
        open: true,
        message: t('hostPasses.removedSuccess', 'Pass offer removed'),
        severity: 'success',
      });
      setRemoveTarget(null);
    } catch (err) {
      setSnackbar({
        open: true,
        message: t('hostPasses.removeError', 'Failed to remove pass offer'),
        severity: 'error',
      });
      console.error(`Error removing host pass offer ${removeTarget.id}:`, err);
    } finally {
      setRemoving(false);
    }
  };

  const openRemoveDialog = (event: React.MouseEvent | null, pass: PassOfferSummary) => {
    event?.stopPropagation();
    closeMenu();
    setRemoveTarget(pass);
  };

  const handleRowNavigate = (pass: PassOfferSummary) => {
    navigate(`/host/passes/${pass.id}`);
  };

  const handleEdit = (event: React.MouseEvent | null, pass: PassOfferSummary) => {
    event?.stopPropagation();
    closeMenu();
    navigate(`/host/passes/${pass.id}/edit`);
  };

  const handleDuplicate = (event: React.MouseEvent | null, pass: PassOfferSummary) => {
    event?.stopPropagation();
    closeMenu();
    const title = nextPassCopyTitle(pass.title, [...offerPasses, ...archivedPasses]);
    navigate('/host/passes/new', {
      state: {
        duplicateFrom: {
          ...pass,
          title,
        },
      } satisfies PassDuplicateLocationState,
    });
  };

  const soldSummary = (pass: PassOfferSummary) => (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" noWrap>
        {t('hostPasses.soldCount', '{{count}} sold', { count: purchaseCountOf(pass) })}
      </Typography>
      <Typography variant="caption" color="text.secondary" noWrap display="block">
        {t('hostPasses.activeInstanceCount', '{{count}} active', { count: activeInstanceCountOf(pass) })}
      </Typography>
    </Box>
  );

  if (loading && passes.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, pb: { xs: 8, md: 10 }, maxWidth: 1600, width: '100%', mx: 'auto' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card elevation={3} sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4" component="h1">
            {t('hostPasses.pageTitle', 'Passes')}
          </Typography>
          {listTab === 'offers' ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/host/passes/new')}
            >
              {t('hostPasses.add', 'Add pass')}
            </Button>
          ) : null}
        </Box>

        <Tabs
          value={listTab}
          onChange={(_event, value: ListTab) => setListTab(value)}
          sx={{ mb: 0 }}
        >
          <Tab value="offers" label={`${t('hostPasses.tabCurrent', 'Current')} (${offerPasses.length})`} />
          <Tab value="archive" label={`${t('hostPasses.tabArchive', 'Archive')} (${archivedPasses.length})`} />
        </Tabs>

        <Divider sx={{ mb: 1 }} />

        {passes.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="body1" color="text.secondary">
              {listTab === 'archive'
                ? t('hostPasses.noArchivedPasses', 'No archived passes')
                : t('hostPasses.noPasses', 'No passes yet')}
            </Typography>
          </Box>
        ) : isMobile ? (
          <Stack spacing={2}>
            {passes.map((pass) => {
              const unitRate = priceUnitRateLine(pass, t);
              const targetSummary = passTargetSummaryFromIds(pass.targetIds, targetById);
              return (
                <Card
                  key={pass.id}
                  variant="outlined"
                  sx={{ p: 2, cursor: 'pointer' }}
                  onClick={() => handleRowNavigate(pass)}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, gap: 1 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h6">{pass.title}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {passSecondaryLine(pass, t, targetSummary)}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5} alignItems="center" onClick={(event) => event.stopPropagation()}>
                      <PassTargetTypeChip targetType={pass.targetType} />
                      <PassOfferStatusChip status={pass.status} />
                      {statusLoadingId === pass.id ? (
                        <CircularProgress size={20} />
                      ) : (
                        <IconButton
                          size="small"
                          aria-label={t('hostPasses.moreActions', 'More actions')}
                          onClick={(event) => openMenu(event, pass)}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                  </Box>
                  <Typography variant="body2">
                    {pass.fixedPrice} {pass.currencyCode}
                    {unitRate ? (
                      <Typography component="span" variant="body2" color="text.secondary">
                        {' · '}
                        {unitRate}
                      </Typography>
                    ) : null}
                  </Typography>
                  <Box sx={{ mt: 1.5 }}>
                    {soldSummary(pass)}
                  </Box>
                </Card>
              );
            })}
          </Stack>
        ) : (
          <TableContainer>
            <Table size="medium">
              <TableHead>
                <TableRow>
                  <TableCell>{t('hostPasses.columnPass', 'Pass')}</TableCell>
                  <TableCell>{t('hostPasses.columnType', 'Type')}</TableCell>
                  <TableCell>{t('hostPasses.price', 'Price')}</TableCell>
                  <TableCell>{t('hostPasses.columnSold', 'Sold')}</TableCell>
                  <TableCell>{t('hostPasses.status', 'Status')}</TableCell>
                  <TableCell align="center" sx={{ width: 88 }}>
                    {t('hostPasses.actions', 'Actions')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {passes.map((pass) => {
                  const unitRate = priceUnitRateLine(pass, t);
                  const targetSummary = passTargetSummaryFromIds(pass.targetIds, targetById);
                  return (
                    <TableRow
                      key={pass.id}
                      hover
                      onClick={() => handleRowNavigate(pass)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {pass.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {passSecondaryLine(pass, t, targetSummary)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <PassTargetTypeChip targetType={pass.targetType} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {pass.fixedPrice} {pass.currencyCode}
                        </Typography>
                        {unitRate ? (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {unitRate}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>{soldSummary(pass)}</TableCell>
                      <TableCell>
                        <PassOfferStatusChip status={pass.status} />
                      </TableCell>
                      <TableCell align="center" onClick={(event) => event.stopPropagation()}>
                        {statusLoadingId === pass.id ? (
                          <CircularProgress size={20} />
                        ) : (
                          <Tooltip title={t('hostPasses.moreActions', 'More actions')}>
                            <IconButton
                              size="small"
                              aria-label={t('hostPasses.moreActions', 'More actions')}
                              onClick={(event) => openMenu(event, pass)}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        {menuPass && menuPass.status !== 'ARCHIVED' ? (
          <MenuItem onClick={(event) => handleEdit(event, menuPass)}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('common.edit', 'Edit')}</ListItemText>
          </MenuItem>
        ) : null}
        {menuPass?.status === 'INACTIVE' ? (
          <MenuItem onClick={(event) => handleActivate(event, menuPass.id)}>
            <ListItemIcon>
              <CheckCircleOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('hostPasses.activate', 'Activate')}</ListItemText>
          </MenuItem>
        ) : null}
        {menuPass?.status === 'ACTIVE' ? (
          <MenuItem onClick={(event) => handleDeactivate(event, menuPass.id)}>
            <ListItemIcon>
              <PauseCircleOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('hostPasses.deactivate', 'Deactivate')}</ListItemText>
          </MenuItem>
        ) : null}
        {menuPass?.status === 'ARCHIVED' ? (
          <MenuItem onClick={(event) => handleRestore(event, menuPass.id)}>
            <ListItemIcon>
              <UnarchiveOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('hostPasses.restore', 'Restore')}</ListItemText>
          </MenuItem>
        ) : null}
        {menuPass ? (
          <MenuItem onClick={(event) => handleDuplicate(event, menuPass)}>
            <ListItemIcon>
              <ContentCopyOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('hostPasses.duplicate', 'Duplicate')}</ListItemText>
          </MenuItem>
        ) : null}
        {menuPass && menuPass.status !== 'ARCHIVED' ? (
          <MenuItem onClick={(event) => handleArchive(event, menuPass.id)}>
            <ListItemIcon>
              <ArchiveOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('hostPasses.archive', 'Archive')}</ListItemText>
          </MenuItem>
        ) : null}
        {menuPass?.status === 'ARCHIVED' ? (
          <MenuItem onClick={(event) => openRemoveDialog(event, menuPass)}>
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('hostPasses.remove', 'Remove')}</ListItemText>
          </MenuItem>
        ) : null}
      </Menu>

      <Dialog open={Boolean(removeTarget)} onClose={() => (!removing ? setRemoveTarget(null) : undefined)}>
        <DialogTitle>{t('hostPasses.removeTitle', 'Remove this pass?')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(
              'hostPasses.removeConfirmBody',
              'It will disappear from Passes and can’t be bought anymore. Passes already purchased stay active and remain usable until they expire or run out.'
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveTarget(null)} disabled={removing}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button color="error" variant="contained" onClick={handleConfirmRemove} disabled={removing}>
            {removing ? <CircularProgress size={20} /> : t('hostPasses.remove', 'Remove')}
          </Button>
        </DialogActions>
      </Dialog>

      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default HostPassesPage;
